export type Theme = 'default' | 'outline' | 'link';
export type Color = 'red' | 'green' | 'orange' | 'blue' | 'yellow' | 'white';
export type Size = 'xs' | 'sm' | 'md';

export const buttonStyles = (theme: Theme = 'default', color: Color = 'red', size: Size = 'md'): string => {
    const styles = [
        'relative',
        'flex',
        'justify-center',
        'items-center',
        'disabled:bg-medium-gray',
        'disabled:cursor-not-allowed',
        'font-bold',
        'gap-2',
        'group',
        'ring-inset',
        'overflow-hidden',
    ];

    switch (size) {
        case 'xs':
            styles.push('px-2', 'py-1', 'text-sm');
            break;
        case 'sm':
            styles.push('px-3', 'py-2', 'text-base');
            break;
        case 'md':
        default:
            styles.push('px-4', 'py-3', 'text-lg');
            break;
    }

    switch (theme) {
        case 'outline':
            styles.push('bg-none', 'ring-2');

            switch (color) {
                case 'green':
                    styles.push('ring-green text-green');
                    break;
                case 'blue':
                    styles.push('ring-blue text-blue');
                    break;
                case 'orange':
                    styles.push('ring-orange text-orange');
                    break;
                case 'yellow':
                    styles.push('ring-yellow text-yellow');
                    break;
                case 'white':
                    styles.push('ring-white text-white');
                    break;
                default:
                    styles.push('ring-red text-red');
            }

            break;
        case 'link':
            switch (color) {
                case 'green':
                    styles.push('text-green group-hover:text-green-brightened');
                    break;
                case 'blue':
                    styles.push('text-blue group-hover:text-dark-blue');
                    break;
                case 'orange':
                    styles.push('text-orange group-hover:text-orange-brightened');
                    break;
                case 'yellow':
                    styles.push('text-yellow group-hover:text-yellow-brightened');
                    break;
                case 'white':
                    styles.push('text-white group-hover:text-white-brightened');
                    break;
                default:
                    styles.push('text-red group-hover:text-red-brightened');
            }

            break;
        default:
            switch (color) {
                case 'green':
                    styles.push('bg-green text-white');
                    break;
                case 'blue':
                    styles.push('bg-blue text-white');
                    break;
                case 'orange':
                    styles.push('bg-orange text-white');
                    break;
                case 'yellow':
                    styles.push('bg-yellow text-white');
                    break;
                case 'white':
                    styles.push('bg-white text-black');
                    break;
                default:
                    styles.push('bg-red text-white');
            }
    }

    return styles.join(' ');
};

export const buttonSliderStyles = (theme: Theme = 'default', color: Color = 'red'): string => {
    const styles = [
        'z-0',
        'absolute',
        'left-0',
        'top-0',
        '-translate-x-full',
        'group-hover:translate-x-0',
        'transition-transform',
        'duration-300',
        'h-full',
        'w-full',
        'group-disabled:hidden',
    ];

    if (theme === 'default') {
        switch (color) {
            case 'green':
                styles.push('bg-green-darkened');
                break;
            case 'blue':
                styles.push('bg-dark-blue');
                break;
            case 'orange':
                styles.push('bg-orange-darkened');
                break;
            case 'yellow':
                styles.push('bg-yellow-darkened');
                break;
            case 'white':
                styles.push('bg-light-gray');
                break;
            default:
                styles.push('bg-red-darkened');
        }
    } else {
        switch (color) {
            case 'green':
                styles.push('bg-green');
                break;
            case 'blue':
                styles.push('bg-dark');
                break;
            case 'orange':
                styles.push('bg-orange');
                break;
            case 'yellow':
                styles.push('bg-yellow');
                break;
            case 'white':
                styles.push('bg-white');
                break;
            default:
                styles.push('bg-red');
        }
    }

    return styles.join(' ');
};

export const buttonTextStyles = (theme: Theme = 'default', color: Color = 'red'): string => {
    const styles = ['z-10', 'transition-colors', 'duration-300', 'flex', 'items-center', 'justify-center', 'gap-2'];

    if (theme !== 'default') {
        if (color === 'white') {
            styles.push('group-hover:text-black');
        } else {
            styles.push('group-hover:text-white');
        }
    } else {
        styles.push('group-hover:text-white');
    }

    return styles.join(' ');
};
